// Scrollable transcript. Grows naturally — the thinking/working strip renders
// straight after the last message, no reserved space below. The full-width
// scroll container wraps an inner max-width column, so messages stay centered
// while the scrollbar hugs the panel edge.

import { useEffect, useRef, useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { ArrowDown } from '@kula-ai/icons-core';
import { Message } from '../Message';
import { SpinnerMessage } from '../SpinnerMessage';
import {
  ConnectionPill,
  QueuedMessageStrip,
  TurnErrorStrip,
  UploadProgressStrip,
} from '../ThreadNotices';
import { ThreadBodySkeleton } from '../ThreadBodySkeleton';
import { FloatingSuggestedActions } from '../../../components/floatingActions';
import { isInProgress, isUserMessage } from '../../utils/messageUtils';
import type { ThreadBodyProps } from './ThreadBody.types';

export const ThreadBody = ({
  messages,
  scrollRef,
  bodyContainerProps,
  isAwaitingReply,
  isInitialLoading,
  streamingSubSteps,
}: ThreadBodyProps) => {
  // Show the "Thinking…" card while we're waiting and the last message is
  // the user's. Once text starts streaming the draft becomes the last
  // message, so this turns off automatically.
  const lastMessage = messages[messages.length - 1];
  const showThinking =
    isAwaitingReply && !!lastMessage && isUserMessage(lastMessage);

  // After the draft renders, the turn may still be running (composing, more
  // tools queued). Keep the same StepTimeline indicator up so the "thinking"
  // UI is one consistent component rather than a separate strip.
  const showWorkingStrip = isAwaitingReply && !showThinking;

  // Jump-to-latest: the send UX pins the user's question to the TOP, so a
  // long streamed reply grows below the fold — and scrolling up mid-stream
  // used to leave no way back to the live tail but manual scrolling.
  const [atBottom, setAtBottom] = useState(true);
  const atBottomRafRef = useRef<number | null>(null);
  useEffect(() => {
    const el = scrollRef?.current;
    if (!el) return undefined;
    const measure = () =>
      setAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 80);
    const onScroll = () => {
      if (atBottomRafRef.current !== null) return;
      atBottomRafRef.current = requestAnimationFrame(() => {
        atBottomRafRef.current = null;
        measure();
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    // Initial measurement — deferred a frame (no sync setState in effect).
    atBottomRafRef.current = requestAnimationFrame(() => {
      atBottomRafRef.current = null;
      measure();
    });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (atBottomRafRef.current !== null)
        cancelAnimationFrame(atBottomRafRef.current);
    };
  }, [scrollRef]);
  const jumpToLatest = () => {
    const el = scrollRef?.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  };

  return (
    <Box pos="relative" flex="1" minH={0} display="flex" flexDirection="column">
      {/* Fade the scroll edges into the white surface so content dissolves
          under the header / composer instead of hard-cutting. */}
      <Box
        aria-hidden
        pos="absolute"
        top={0}
        left={0}
        right={0}
        h="24px"
        bgGradient="linear(to-b, base-white, transparent)"
        pointerEvents="none"
        zIndex={1}
      />
      <Box
        aria-hidden
        data-test-id="thread-body-bottom-fade"
        pos="absolute"
        bottom={0}
        left={0}
        right={0}
        h="32px"
        bgGradient="linear(to-t, base-white, transparent)"
        pointerEvents="none"
        zIndex={1}
      />
      <Box
        ref={scrollRef}
        role="log"
        aria-label="Conversation"
        aria-live="polite"
        flex="1"
        minH={0}
        overflowY="auto"
        px="16px"
        py="16px"
      >
        <Box mx="auto" {...bodyContainerProps}>
          {isInitialLoading ? (
            <ThreadBodySkeleton />
          ) : (
            <Flex direction="column" gap="20px" pb="32px">
              {messages.map((message) => (
                <Box key={message.id} data-message-id={message.id}>
                  {isInProgress(message) ? (
                    <SpinnerMessage detail={message.content || undefined} />
                  ) : (
                    <Message message={message} />
                  )}
                </Box>
              ))}
              {showThinking ? (
                <Box data-test-id="thread-thinking">
                  <SpinnerMessage
                    startedAt={lastMessage.created_at}
                    subSteps={streamingSubSteps}
                  />
                </Box>
              ) : null}
              <TurnErrorStrip />
              <QueuedMessageStrip />
              <UploadProgressStrip />
              <ConnectionPill />
              {showWorkingStrip ? (
                <Box data-test-id="thread-working-strip">
                  <SpinnerMessage subSteps={streamingSubSteps} />
                </Box>
              ) : null}
            </Flex>
          )}
        </Box>
      </Box>
      {/* Bottom-anchored controls, stacked above the composer: the jump pill
          centered on top, the suggested-action bar left-aligned below it so it
          starts where the message content does. */}
      <Box
        position="absolute"
        bottom="12px"
        left="16px"
        right="16px"
        pointerEvents="none"
      >
        {/* Same centered max-width column as the transcript, so the bar starts
            where the message content does rather than at the panel edge. */}
        <Flex
          direction="column"
          gap="8px"
          mx="auto"
          {...bodyContainerProps}
          sx={{ '& > * > *': { pointerEvents: 'auto' } }}
        >
          {!atBottom && (
            <Flex justify="center">
              <Box
                as="button"
                type="button"
                onClick={jumpToLatest}
                display="inline-flex"
                alignItems="center"
                gap="4px"
                px="10px"
                py="6px"
                borderRadius="full"
                bg="base-white"
                border="1px solid"
                borderColor="border-color"
                boxShadow="0 2px 8px rgba(0,0,0,0.08)"
                color="secondary-700"
                fontSize="12px"
                fontWeight="500"
                cursor="pointer"
                _hover={{ bg: 'secondary-50' }}
                aria-label="Jump to latest"
                data-test-id="thread-jump-to-latest"
              >
                <ArrowDown size={12} color="currentColor" />
                Latest
              </Box>
            </Flex>
          )}
          <Flex justify="flex-start" minW={0}>
            <FloatingSuggestedActions active={!atBottom} />
          </Flex>
        </Flex>
      </Box>
    </Box>
  );
};
